import { useState, useEffect, useRef } from "react";
import { supabase } from "./db";

// 👇 Zoznam DEMO účtov
const DEMO_ROLES = [
  {
    id: "supplier",
    label: "Dodávateľ",
    email: "dodavatel@demo.sk",
    pass: "123456",
  },
  {
    id: "admin",
    label: "Obchodný manažér",
    email: "admin1@1.sk",
    pass: "123456",
  },
  {
    id: "master",
    label: "Senior obchodný manažér",
    email: "manager@1.sk",
    pass: "123456",
  },
  {
    id: "sku",
    label: "Správca číselníka",
    email: "ciselnik@1.sk",
    pass: "123456",
  },
  {
    id: "content",
    label: "Content špecialista",
    email: "content@1.sk",
    pass: "123456",
  },
];

export default function Login() {
  // Stavy pre login
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Stavy pre kontaktný formulár
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  const [isMobile, setIsMobile] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);

  const [activeDemo, setActiveDemo] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 850);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) setError("Nesprávny email alebo heslo.");
    setLoading(false);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    const finalMessage =
      contactMessage.trim() !== "" ? contactMessage : "Mám záujem o platformu.";
    const { error } = await supabase
      .from("contact_requests")
      .insert([{ email: contactEmail, message: finalMessage }]);
    setContactLoading(false);

    if (!error) {
      setContactSuccess(true);
      setContactEmail("");
      setContactMessage("");
      setTimeout(() => setContactSuccess(false), 5000);
    } else {
      alert("Nastala chyba pri odosielaní, skúste to znova.");
    }
  };

  const handleScroll = () => {
    // Na mobile nechceme prepínať aktívne sekcie (aby nezmizli)
    if (containerRef.current && !isMobile) {
      const { scrollTop, clientHeight } = containerRef.current;
      const sectionIndex = Math.round(scrollTop / clientHeight);
      if (sectionIndex !== currentSection) {
        setCurrentSection(sectionIndex);
      }
    }
  };

  const scrollToSection = (index) => {
    if (containerRef.current) {
      if (isMobile) {
        // Na mobile len posunieme scroll, nepočítame presné výšky okna
        const sections = containerRef.current.querySelectorAll("section");
        if (sections[index]) {
          sections[index].scrollIntoView({ behavior: "smooth" });
        }
      } else {
        const clientHeight = containerRef.current.clientHeight;
        containerRef.current.scrollTo({
          top: index * clientHeight,
          behavior: "smooth",
        });
      }
    }
  };

  const handleDemoClick = (role) => {
    setActiveDemo(role);
    setEmail(role.email);
    setPassword(role.pass);
    setError("");

    if (isMobile && containerRef.current) {
      containerRef.current.scrollBy({ top: -150, behavior: "smooth" });
    }
  };

  const s = getStyles(isMobile, currentSection);

  const isDarkBackground = currentSection === 0 || currentSection === 2;
  const inactiveDotColor = isDarkBackground
    ? "rgba(255, 255, 255, 0.3)"
    : "rgba(15, 23, 42, 0.2)";

  // 👇 OPRAVA: Pre mobilné zariadenia vypíname "skrývanie" sekcií, nech sú všetky viditeľné pod sebou
  const transitionStyle = (index) => {
    if (isMobile) {
      return {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
      };
    }
    return {
      height: "100%",
      width: "100%",
      opacity: currentSection === index ? 1 : 0,
      transform:
        currentSection === index
          ? "translateY(0) scale(1)"
          : "translateY(50px) scale(0.98)",
      transition:
        "opacity 0.8s ease-out, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
      pointerEvents: currentSection === index ? "auto" : "none",
    };
  };

  return (
    <div style={s.snapContainer} ref={containerRef} onScroll={handleScroll}>
      {/* Na mobile bublinkovú navigáciu skryjeme */}
      {!isMobile && (
        <div style={s.paginationContainer}>
          {[0, 1, 2, 3].map((index) => (
            <div
              key={index}
              onClick={() => scrollToSection(index)}
              style={{
                ...s.dot,
                backgroundColor:
                  currentSection === index ? "#3b82f6" : inactiveDotColor,
                transform: currentSection === index ? "scale(1.5)" : "scale(1)",
              }}
            />
          ))}
        </div>
      )}

      <style>
        {`
          @keyframes floatBlob1 {
            0% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
            25% { transform: translate(120px, -130px) scale(1.15) rotate(5deg); }
            50% { transform: translate(-60px, -20px) scale(0.9) rotate(-5deg); }
            75% { transform: translate(40px, 70px) scale(1.1) rotate(2deg); }
            100% { transform: translate(0px, 0px) scale(1) rotate(0deg); }
          }
          @keyframes floatBlob2 {
            0% { transform: translate(0px, 0px) scale(1); }
            50% { transform: translate(-140px, 100px) scale(1.2); }
            100% { transform: translate(0px, 0px) scale(1); }
          }
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(10px); }
          }
          ::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>

      {/* =========================================
          SEKCIA 1: HERO & LOGIN
          ========================================= */}
      <section style={s.snapSection}>
        <div style={transitionStyle(0)}>
          <div style={s.container}>
            {/* Tmavý panel s textom */}
            <div style={s.leftPanel}>
              <div style={s.leftContent}>
                <div style={s.badge}>🚀 B2B Portál V2.0</div>
                <h1 style={s.heading}>
                  <span style={{ color: "#ffffff" }}>Listing produktov</span>{" "}
                  <br />
                  <span style={s.highlight}>pod kontrolou</span>
                </h1>
                <p style={s.subtitle}>
                  Získajte od dodávateľov maximum produktových dát, filtrov a
                  atribútov. Dodávatelia, majte neustály prehľad o stave
                  listingu vašich produktov u partnerov.
                </p>
                <div style={s.statsContainer}>
                  <div style={s.statCard}>
                    <h3 style={s.statNumber}>100%</h3>
                    <p style={s.statText}>Digitalizácia</p>
                  </div>
                  <div style={s.statCard}>
                    <h3 style={s.statNumber}>40+</h3>
                    <p style={s.statText}>Parametrov</p>
                  </div>
                  <div style={s.statCard}>
                    <h3 style={s.statNumber}>24/7</h3>
                    <p style={s.statText}>Prehľad</p>
                  </div>
                </div>
              </div>
              <div style={{ ...s.blob, ...s.blobS1_1 }}></div>
              <div style={{ ...s.blob, ...s.blobS1_2 }}></div>

              {!isMobile && (
                <div
                  style={s.scrollIndicator}
                  onClick={() => scrollToSection(1)}
                >
                  <div style={s.scrollArrow}>↓</div>
                  <span
                    style={{ fontSize: "12px", opacity: 0.7, marginTop: "5px" }}
                  >
                    Zistiť viac
                  </span>
                </div>
              )}
            </div>

            {/* Svetlý panel s loginom a demom */}
            <div style={s.rightPanel}>
              <div style={s.rightContentWrapper}>
                <div style={s.loginCard}>
                  <div style={{ marginBottom: "30px", textAlign: "center" }}>
                    <h2
                      style={{
                        margin: "0 0 10px 0",
                        fontSize: "28px",
                        color: "#0f172a",
                      }}
                    >
                      Vitajte na portáli 👋
                    </h2>
                    <p
                      style={{ margin: 0, color: "#64748b", fontSize: "15px" }}
                    >
                      Prihláste sa do svojho účtu pre správu produktov.
                    </p>
                  </div>
                  {error && <div style={s.errorBox}>{error}</div>}
                  <form
                    onSubmit={handleLogin}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                    }}
                  >
                    <div>
                      <label style={s.label}>Pracovný e-mail</label>
                      <input
                        type="email"
                        required
                        placeholder="napr. meno@firma.sk"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={s.input}
                      />
                    </div>
                    <div>
                      <label style={s.label}>Heslo</label>
                      <input
                        type="password"
                        required
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={s.input}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      style={s.primaryButton}
                    >
                      {loading ? "Prihlasujem..." : "Prihlásiť sa do portálu"}
                    </button>
                  </form>
                </div>

                <div style={s.demoSection}>
                  <h3 style={s.demoHeading}>
                    Vyskúšajte platformu a prihláste sa ako:
                  </h3>
                  <div style={s.demoGrid}>
                    {DEMO_ROLES.map((role) => {
                      const isActive = activeDemo?.id === role.id;
                      return (
                        <button
                          key={role.id}
                          type="button"
                          onClick={() => handleDemoClick(role)}
                          style={{
                            ...s.demoBtn,
                            backgroundColor: isActive ? "#0f172a" : "white",
                            color: isActive ? "white" : "#475569",
                            borderColor: isActive ? "#0f172a" : "#cbd5e1",
                            boxShadow: isActive
                              ? "0 4px 10px rgba(15, 23, 42, 0.15)"
                              : "none",
                          }}
                        >
                          {role.label}
                        </button>
                      );
                    })}
                  </div>

                  {activeDemo && (
                    <div style={s.demoAlertBox}>
                      <div style={s.demoAlertTitle}>
                        Prihláste sa pod demo účtom:
                      </div>
                      <div style={s.demoAlertInner}>
                        <div style={{ marginBottom: "5px" }}>
                          <span style={s.demoAlertLabel}>Email:</span>{" "}
                          <strong
                            style={{ color: "#0f172a", wordBreak: "break-all" }}
                          >
                            {activeDemo.email}
                          </strong>
                        </div>
                        <div>
                          <span style={s.demoAlertLabel}>Heslo:</span>{" "}
                          <strong style={{ color: "#0f172a" }}>
                            {activeDemo.pass}
                          </strong>
                        </div>
                      </div>
                      <div style={s.demoAlertFooter}>
                        ✓ Údaje boli predvyplnené do formulára.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          SEKCIA 2: BENEFITY
          ========================================= */}
      <section style={{ ...s.snapSection, backgroundColor: "#ffffff" }}>
        <div style={transitionStyle(1)}>
          <div style={s.infoSection}>
            <div style={{ ...s.blob, ...s.blobS2_1 }}></div>
            <div style={{ ...s.blob, ...s.blobS2_2 }}></div>

            <div style={s.infoContent}>
              <div style={s.badgeDark}>💡 Prečo zvoliť náš ekosystém?</div>
              <h2 style={{ ...s.infoHeading, color: "#0f172a" }}>
                Koniec chaosu v{" "}
                <span style={{ color: "#3b82f6" }}>Excel tabuľkách</span>
              </h2>
              <p style={s.infoText}>
                Náš portál prepája nákupcov a dodávateľov do jedného plynulého
                prostredia. Zabudnite na posielanie chýbajúcich EAN kódov cez
                email. Všetko je centralizované.
              </p>

              <div style={s.featuresGrid}>
                <div style={s.featureBoxLight}>
                  <div style={s.featureIcon}>📋</div>
                  <h3 style={s.featureTitleDark}>Unifikovaný Listing</h3>
                  <p style={s.featureDescDark}>
                    Dáta sa vypĺňajú do prísne definovanej štruktúry. Koniec
                    chýbajúcim poliam.
                  </p>
                </div>
                <div style={s.featureBoxLight}>
                  <div style={s.featureIcon}>⚡</div>
                  <h3 style={s.featureTitleDark}>Hromadný Import</h3>
                  <p style={s.featureDescDark}>
                    Nahrajte stovky produktov naraz cez náš inteligentný
                    validátor.
                  </p>
                </div>
                <div style={s.featureBoxLight}>
                  <div style={s.featureIcon}>✅</div>
                  <h3 style={s.featureTitleDark}>Bleskové schvaľovanie</h3>
                  <p style={s.featureDescDark}>
                    Nákupcovia vidia nové produkty v reálnom čase a presúvajú
                    ich do e-shopu.
                  </p>
                </div>
                <div style={s.featureBoxLight}>
                  <div style={s.featureIcon}>📦</div>
                  <h3 style={s.featureTitleDark}>Skladové Karty</h3>
                  <p style={s.featureDescDark}>
                    Transparentný proces pridelovania SKU a sledovania stavu
                    obsahu.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          SEKCIA 3: AKO TO FUNGUJE
          ========================================= */}
      <section style={{ ...s.snapSection, backgroundColor: "#0f172a" }}>
        <div style={transitionStyle(2)}>
          <div style={s.infoSection}>
            <div style={{ ...s.blob, ...s.blobS3_1 }}></div>
            <div style={{ ...s.blob, ...s.blobS3_2 }}></div>

            <div style={s.infoContent}>
              <div style={s.badge}>⚙️ Proces listingu</div>
              <h2 style={{ ...s.infoHeading, color: "#ffffff" }}>
                Od zadania po predaj v{" "}
                <span style={s.highlight}>3 krokoch</span>
              </h2>
              <p style={{ ...s.infoText, color: "#94a3b8" }}>
                Navrhnuté tak, aby dodávatelia strávili administráciou minimum
                času a nákupcovia dostali 100% čisté dáta.
              </p>

              <div style={s.stepsGrid}>
                <div style={s.stepCard}>
                  <div style={s.stepNumber}>1</div>
                  <h3 style={s.stepTitle}>Výber a Šablóna</h3>
                  <p style={s.stepDesc}>
                    Dodávateľ zvolí kategóriu a stiahne si dynamicky
                    vygenerovanú šablónu presne na mieru.
                  </p>
                </div>
                <div style={s.stepCard}>
                  <div style={s.stepNumber}>2</div>
                  <h3 style={s.stepTitle}>Nahratie a Validácia</h3>
                  <p style={s.stepDesc}>
                    Systém skontroluje EAN kódy, ceny, DPH a rozmery. Zamedzí
                    chybám pred uložením.
                  </p>
                </div>
                <div style={s.stepCard}>
                  <div style={s.stepNumber}>3</div>
                  <h3 style={s.stepTitle}>Schválenie</h3>
                  <p style={s.stepDesc}>
                    Nákupca produkt schváli, systém pridelí interné SKU a tovar
                    je pripravený pre e-shop.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          SEKCIA 4: CALL TO ACTION (Formulár)
          ========================================= */}
      <section style={{ ...s.snapSection, backgroundColor: "#f8fafc" }}>
        <div style={transitionStyle(3)}>
          <div style={s.ctaSection}>
            <div style={{ ...s.blob, ...s.blobS4 }}></div>

            <div style={s.ctaContent}>
              <h2
                style={{
                  fontSize: isMobile ? "36px" : "56px",
                  fontWeight: "800",
                  color: "#0f172a",
                  marginBottom: "20px",
                  letterSpacing: "-1px",
                }}
              >
                Pripravený zrýchliť <br /> váš{" "}
                <span style={{ color: "#3b82f6" }}>predajný proces?</span>
              </h2>
              <p
                style={{
                  fontSize: isMobile ? "16px" : "20px",
                  color: "#64748b",
                  marginBottom: "30px",
                  maxWidth: "600px",
                  margin: "0 auto 30px auto",
                }}
              >
                Zanechajte nám na seba kontakt. Prejdeme si vaše potreby a
                ukážeme vám portál v akcii.
              </p>

              <div style={s.contactFormContainer}>
                {contactSuccess ? (
                  <div style={s.successBadge}>
                    ✅ Ďakujeme, váš záujem sme zaznamenali! Čoskoro sa vám
                    ozveme.
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} style={s.contactForm}>
                    <input
                      type="email"
                      required
                      placeholder="Váš pracovný e-mail *"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      style={s.contactInput}
                    />
                    <textarea
                      placeholder="Mám záujem o platformu."
                      value={contactMessage}
                      onChange={(e) => setContactMessage(e.target.value)}
                      style={s.contactTextarea}
                      rows="3"
                    />
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        width: "100%",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <button
                        type="submit"
                        disabled={contactLoading}
                        style={s.ctaButtonPrimary}
                      >
                        {contactLoading ? "Odosielam..." : "✉️ Odoslať záujem"}
                      </button>
                      {isMobile && (
                        <button
                          type="button"
                          onClick={() => scrollToSection(0)}
                          style={s.ctaButtonSecondary}
                        >
                          Späť nahor
                        </button>
                      )}
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// 🔥 OPRAVENÉ ŠTÝLY PRE MOBIL A PC
const getStyles = (isMobile, currentSection) => ({
  snapContainer: {
    height: "100vh",
    width: "100vw",
    // 👇 KĽÚČOVÁ ZMENA PRE MOBIL: Voľný scroll miesto Snapu
    overflowY: "auto",
    scrollSnapType: isMobile ? "none" : "y mandatory",
    scrollBehavior: "smooth",
    margin: 0,
    padding: 0,
    fontFamily: "'Inter', sans-serif",
    overflowX: "hidden",
    position: "relative",
  },
  snapSection: {
    // 👇 Na mobile necháme sekcie voľne rásť
    minHeight: "100vh",
    height: isMobile ? "auto" : "100vh",
    width: "100vw",
    scrollSnapAlign: "start",
    position: "relative",
    overflow: isMobile ? "visible" : "hidden",
    display: "flex",
    flexDirection: "column",
  },

  paginationContainer: {
    position: "fixed",
    left: "30px",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    zIndex: 100,
  },
  dot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },

  blob: {
    position: "absolute",
    borderRadius: "50%",
    zIndex: 1,
    filter: "blur(40px)",
  },

  blobS1_1: {
    top: "-10%",
    left: "-10%",
    width: isMobile ? "300px" : "500px",
    height: isMobile ? "300px" : "500px",
    background:
      "radial-gradient(circle, rgba(59,130,246,0.3) 0%, rgba(15,23,42,0) 70%)",
    animation: "floatBlob1 8s ease-in-out infinite",
  },
  blobS1_2: {
    bottom: "-10%",
    right: "-10%",
    width: isMobile ? "350px" : "600px",
    height: isMobile ? "350px" : "600px",
    background:
      "radial-gradient(circle, rgba(139,92,246,0.2) 0%, rgba(15,23,42,0) 70%)",
  },

  blobS2_1: {
    top: "20%",
    right: "-5%",
    width: "400px",
    height: "400px",
    background:
      "radial-gradient(circle, rgba(56,189,248,0.15) 0%, rgba(255,255,255,0) 70%)",
  },
  blobS2_2: {
    bottom: "-10%",
    left: "-10%",
    width: "500px",
    height: "500px",
    background:
      "radial-gradient(circle, rgba(99,102,241,0.1) 0%, rgba(255,255,255,0) 70%)",
  },

  blobS3_1: {
    top: "-20%",
    left: "30%",
    width: "600px",
    height: "600px",
    background:
      "radial-gradient(circle, rgba(236,72,153,0.15) 0%, rgba(15,23,42,0) 70%)",
  },
  blobS3_2: {
    bottom: "-20%",
    right: "10%",
    width: "500px",
    height: "500px",
    background:
      "radial-gradient(circle, rgba(16,185,129,0.15) 0%, rgba(15,23,42,0) 70%)",
  },

  blobS4: {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "800px",
    height: "800px",
    background:
      "radial-gradient(circle, rgba(59,130,246,0.1) 0%, rgba(248,250,252,0) 70%)",
  },

  container: {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    height: "100%",
    minHeight: "100vh",
    width: "100%",
  },
  leftPanel: {
    flex: isMobile ? "none" : "1.2",
    backgroundColor: "#0f172a",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    // 👇 Na mobile spravíme pekné odsadenie, aby to dýchalo
    padding: isMobile ? "60px 20px 40px 20px" : "60px 80px",
    color: "white",
  },
  leftContent: {
    position: "relative",
    zIndex: 10,
    maxWidth: "600px",
    margin: "0 auto",
    paddingLeft: isMobile ? "0" : "30px",
    textAlign: isMobile ? "center" : "left",
  },
  badge: {
    display: "inline-block",
    padding: "8px 16px",
    backgroundColor: "rgba(59, 130, 246, 0.2)",
    color: "#60a5fa",
    borderRadius: "30px",
    fontSize: "14px",
    fontWeight: "bold",
    marginBottom: "20px",
    border: "1px solid rgba(59, 130, 246, 0.3)",
  },
  heading: {
    fontSize: isMobile ? "36px" : "64px",
    fontWeight: "800",
    lineHeight: "1.1",
    margin: "0 0 20px 0",
    letterSpacing: "-1px",
  },
  highlight: {
    background: "linear-gradient(to right, #3b82f6, #8b5cf6)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  subtitle: {
    fontSize: isMobile ? "16px" : "18px",
    lineHeight: "1.6",
    color: "#94a3b8",
    marginBottom: isMobile ? "30px" : "50px",
    maxWidth: "500px",
  },
  statsContainer: {
    display: "flex",
    gap: "15px",
    flexWrap: "wrap",
    justifyContent: isMobile ? "center" : "flex-start",
  },
  statCard: {
    background: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    padding: isMobile ? "15px" : "20px",
    borderRadius: "16px",
    flex: "1",
    minWidth: "100px",
  },
  statNumber: {
    margin: "0 0 5px 0",
    fontSize: isMobile ? "24px" : "28px",
    color: "white",
  },
  statText: { margin: 0, fontSize: "13px", color: "#94a3b8" },

  scrollIndicator: {
    position: "absolute",
    bottom: "30px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "#94a3b8",
    zIndex: 10,
    cursor: "pointer",
  },
  scrollArrow: { fontSize: "24px", animation: "bounce 2s infinite" },

  rightPanel: {
    flex: isMobile ? "none" : "1",
    display: "flex",
    justifyContent: "center",
    alignItems: isMobile ? "flex-start" : "center",
    backgroundColor: "#f8fafc",
    padding: isMobile ? "40px 20px 60px 20px" : "40px",
  },

  rightContentWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    maxWidth: "480px",
    zIndex: 20,
  },

  loginCard: {
    width: "100%",
    background: "white",
    padding: isMobile ? "30px 20px" : "40px",
    borderRadius: "24px",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05)",
    position: "relative",
  },
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: "#334155",
    marginBottom: "8px",
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    fontSize: "15px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    outline: "none",
    boxSizing: "border-box",
  },
  primaryButton: {
    width: "100%",
    padding: "16px",
    backgroundColor: "#0f172a",
    color: "white",
    border: "none",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "bold",
    cursor: "pointer",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    marginTop: "10px",
  },
  errorBox: {
    padding: "12px",
    backgroundColor: "#fef2f2",
    color: "#dc2626",
    borderRadius: "8px",
    fontSize: "14px",
    marginBottom: "20px",
    textAlign: "center",
    border: "1px solid #fecaca",
  },

  demoSection: { marginTop: "30px", width: "100%", textAlign: "center" },
  demoHeading: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#64748b",
    marginBottom: "15px",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  demoGrid: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "10px",
  },
  demoBtn: {
    padding: "8px 16px",
    borderRadius: "20px",
    border: "1px solid",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontFamily: "'Inter', sans-serif",
  },
  demoAlertBox: {
    marginTop: "20px",
    padding: "20px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    textAlign: "left",
    animation: "fadeIn 0.3s ease-out",
  },
  demoAlertTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#334155",
    marginBottom: "12px",
  },
  demoAlertInner: {
    backgroundColor: "white",
    padding: "15px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
  },
  demoAlertLabel: { color: "#64748b", display: "inline-block", width: "50px" },
  demoAlertFooter: {
    marginTop: "12px",
    fontSize: "13px",
    color: "#3b82f6",
    fontWeight: "600",
  },

  infoSection: {
    height: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? "40px 20px" : "80px",
    boxSizing: "border-box",
    position: "relative",
  },
  infoContent: {
    maxWidth: "1000px",
    width: "100%",
    textAlign: "center",
    position: "relative",
    zIndex: 10,
  },
  badgeDark: {
    display: "inline-block",
    padding: "8px 16px",
    backgroundColor: "#f1f5f9",
    color: "#475569",
    borderRadius: "30px",
    fontSize: "14px",
    fontWeight: "bold",
    marginBottom: "20px",
  },
  infoHeading: {
    fontSize: isMobile ? "32px" : "48px",
    fontWeight: "800",
    margin: "0 0 20px 0",
    letterSpacing: "-1px",
  },
  infoText: {
    fontSize: isMobile ? "16px" : "18px",
    lineHeight: "1.6",
    maxWidth: "700px",
    margin: "0 auto 40px auto",
  },

  featuresGrid: {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
    gap: "20px",
    textAlign: "left",
  },
  featureBoxLight: {
    padding: "25px",
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    backdropFilter: "blur(10px)",
    borderRadius: "20px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 10px 30px -10px rgba(0,0,0,0.05)",
  },
  featureIcon: { fontSize: "32px", marginBottom: "15px" },
  featureTitleDark: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#0f172a",
    margin: "0 0 10px 0",
  },
  featureDescDark: {
    fontSize: "14px",
    color: "#64748b",
    margin: 0,
    lineHeight: "1.5",
  },

  stepsGrid: {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
    gap: "20px",
    textAlign: "left",
  },
  stepCard: {
    padding: "30px 20px",
    background: "rgba(255, 255, 255, 0.03)",
    backdropFilter: "blur(16px)",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    borderRadius: "24px",
  },
  stepNumber: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#3b82f6",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    fontWeight: "bold",
    marginBottom: "20px",
  },
  stepTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "white",
    margin: "0 0 10px 0",
  },
  stepDesc: {
    fontSize: "14px",
    color: "#94a3b8",
    margin: 0,
    lineHeight: "1.6",
  },

  ctaSection: {
    height: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? "40px 20px" : "20px",
    position: "relative",
  },
  ctaContent: {
    textAlign: "center",
    position: "relative",
    zIndex: 10,
    width: "100%",
    maxWidth: "600px",
  },

  contactFormContainer: {
    width: "100%",
    maxWidth: "500px",
    margin: "0 auto",
    background: "white",
    padding: isMobile ? "20px" : "30px",
    borderRadius: "20px",
    boxShadow: "0 20px 40px -10px rgba(0,0,0,0.05)",
    border: "1px solid #e2e8f0",
  },
  contactForm: { display: "flex", flexDirection: "column", gap: "15px" },
  contactInput: {
    width: "100%",
    padding: "14px 16px",
    fontSize: "15px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#f8fafc",
    outline: "none",
    boxSizing: "border-box",
  },
  contactTextarea: {
    width: "100%",
    padding: "14px 16px",
    fontSize: "15px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#f8fafc",
    outline: "none",
    boxSizing: "border-box",
    resize: "none",
    fontFamily: "'Inter', sans-serif",
  },
  ctaButtonPrimary: {
    flex: 1,
    padding: "16px 20px",
    backgroundColor: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: "bold",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  ctaButtonSecondary: {
    flex: 1,
    padding: "16px 20px",
    backgroundColor: "white",
    color: "#0f172a",
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: "bold",
    cursor: "pointer",
  },
  successBadge: {
    padding: "20px",
    backgroundColor: "#dcfce7",
    color: "#166534",
    borderRadius: "12px",
    fontWeight: "600",
    border: "1px solid #bbf7d0",
  },
});
